/**
 * Runtime switcher pill — env-coloured indicator that opens a dropdown
 * of all registered runtimes. Reflects + mutates the SAME
 * `ActiveRuntimeContext` everywhere, so the dashboard, monitor pages,
 * and the designer's deploy target are always in sync.
 *
 * Two render modes:
 *   - default (shell topbar)         → full pill with env tag + name
 *   - compact (editor toolbars)      → narrower pill, env tag only
 *
 * The dropdown groups runtimes by promotion chain (tier-sorted
 * dev → test → stag → prod) plus a "Loose runtimes" section, and marks the
 * registry default. "Manage runtimes…" links to /runtimes.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UiIcons } from '@pipebase/ui';
import { useActiveRuntime } from '../runtime/ActiveRuntimeContext';
import { groupRuntimesByChains } from '../utils/promotion';
import type { RuntimeEnvironment, RuntimeSummary } from '../types';

const ENV_VISUAL: Record<
  RuntimeEnvironment,
  { label: string; dot: string; pillBorder: string; pillBg: string }
> = {
  prod: {
    label: 'PROD',
    dot: 'var(--ok)',
    pillBorder: 'rgba(34, 197, 94, 0.45)',
    pillBg: 'rgba(34, 197, 94, 0.10)',
  },
  stag: {
    label: 'STAG',
    dot: 'var(--warn)',
    pillBorder: 'rgba(245, 158, 11, 0.45)',
    pillBg: 'rgba(245, 158, 11, 0.10)',
  },
  test: {
    label: 'TEST',
    dot: 'var(--info)',
    pillBorder: 'rgba(56, 189, 248, 0.45)',
    pillBg: 'rgba(56, 189, 248, 0.10)',
  },
  dev: {
    label: 'DEV',
    dot: 'var(--brand-300)',
    pillBorder: 'rgba(94, 131, 251, 0.45)',
    pillBg: 'rgba(94, 131, 251, 0.10)',
  },
};

interface RuntimeSwitcherProps {
  /** "default" shows env tag + runtime name; "compact" shows env tag only. */
  variant?: 'default' | 'compact';
  /** Override the missing-runtime fallback CTA text. */
  emptyLabel?: string;
}

export function RuntimeSwitcher({
  variant = 'default',
  emptyLabel = 'Add a runtime',
}: RuntimeSwitcherProps) {
  const { runtimes, active, setActive } = useActiveRuntime();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [open]);

  const compact = variant === 'compact';

  if (!active) {
    return (
      <button
        type="button"
        className="btn btn--ghost"
        style={{ height: 30 }}
        onClick={() => navigate('/runtimes')}
      >
        {emptyLabel}
      </button>
    );
  }
  const viz = ENV_VISUAL[active.environment];
  const { chains, loose } = groupRuntimesByChains(runtimes);

  const renderRuntimeButton = (r: RuntimeSummary) => {
    const v = ENV_VISUAL[r.environment];
    const isActive = r.id === active.id;
    return (
      <button
        key={r.id}
        type="button"
        onClick={() => {
          setActive(r.id);
          setOpen(false);
        }}
        data-testid={`runtime-switcher-item-${r.id}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          width: '100%',
          padding: '8px 10px',
          borderRadius: 'var(--r-sm)',
          background: isActive ? 'var(--bg-tint)' : 'transparent',
          border: 0,
          color: 'var(--text-1)',
          cursor: 'pointer',
          textAlign: 'left',
          fontSize: 13,
        }}
        onMouseEnter={(e) => {
          if (!isActive) e.currentTarget.style.background = 'var(--bg-hover)';
        }}
        onMouseLeave={(e) => {
          if (!isActive) e.currentTarget.style.background = 'transparent';
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            minWidth: 50,
            padding: '2px 6px',
            borderRadius: 100,
            background: v.pillBg,
            border: `1px solid ${v.pillBorder}`,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.04em',
          }}
        >
          <span
            style={{ width: 5, height: 5, borderRadius: '50%', background: v.dot }}
          />
          {v.label}
        </span>
        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {r.name}
        </span>
        {r.isDefault && (
          <span className="badge badge--ghost" style={{ fontSize: 10, height: 18 }}>
            default
          </span>
        )}
      </button>
    );
  };

  const groupLabelStyle: React.CSSProperties = {
    padding: '4px 10px',
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--text-4)',
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={`Active runtime: ${active.name} · ${active.environment}`}
        data-testid="runtime-switcher-trigger"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          height: 30,
          padding: compact ? '0 8px 0 8px' : '0 12px 0 10px',
          background: viz.pillBg,
          border: `1px solid ${viz.pillBorder}`,
          borderRadius: 100,
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--text-1)',
          cursor: 'pointer',
          letterSpacing: '0.02em',
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: viz.dot,
            boxShadow: `0 0 6px ${viz.dot}`,
          }}
        />
        <span>{viz.label}</span>
        {!compact && (
          <span style={{ color: 'var(--text-3)', fontWeight: 500, fontSize: 11.5 }}>
            · {active.name}
          </span>
        )}
        <span style={{ color: 'var(--text-4)', marginLeft: compact ? 0 : 2 }}>
          {UiIcons.chevronDown}
        </span>
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            minWidth: 280,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-md)',
            boxShadow: 'var(--shadow-lg)',
            padding: 6,
            zIndex: 60,
          }}
        >
          <div
            style={{
              padding: '6px 10px',
              fontSize: 10.5,
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--text-4)',
            }}
          >
            Switch active runtime
          </div>
          {chains.map((c, i) => (
            <div key={`chain-${i}`}>
              <div style={groupLabelStyle}>
                {c.chain[0]!.name} → {c.chain[c.chain.length - 1]!.name}
              </div>
              {c.chain.map(renderRuntimeButton)}
            </div>
          ))}
          {loose.length > 0 && (
            <div>
              {chains.length > 0 && (
                <div style={groupLabelStyle}>Loose runtimes</div>
              )}
              {loose.map(renderRuntimeButton)}
            </div>
          )}
          <div
            style={{
              borderTop: '1px solid var(--border-subtle)',
              marginTop: 4,
              paddingTop: 4,
            }}
          >
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate('/runtimes');
              }}
              style={{
                display: 'block',
                width: '100%',
                padding: '7px 10px',
                background: 'transparent',
                border: 0,
                color: 'var(--text-2)',
                cursor: 'pointer',
                textAlign: 'left',
                fontSize: 12.5,
              }}
            >
              Manage runtimes…
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
